import { createSelector } from "reselect"
import { get, chain, map, filter, orderBy, find, first, isNumber } from "lodash"
import { getTickerAndMicFromContributor } from "helpers/securities"
import { getAllAvailableRollups } from "helpers/rollups"
import { positionSetContributorsSummaryText } from "helpers/contributors"
import { computeGroupedContributorsCSV, computeContributorsGroupCSV, computeContributorsCSV } from "helpers/csv"
import { computeSelectedGroup } from "helpers/analyze"
import { VIEW_TYPES } from "helpers/base"
import { createQueryStringSelector } from "selectors/queryString"
import { makeGroupSort } from "helpers/contributorsSort"

const EMPTY_ARRAY = []
const EMPTY_OBJECT = {}

const getPositionSetName = (_, props) => props.positionSetName
const getFactorCategoryName = (_, props) => props.factorCategoryName
const getViewType = (_, props) => props.viewType
const getShowNotHeld = (_, props) => props.showNotHeld
const getContributorsData = state => get(state, "global.data.compositeBetaContributors.data.model.simulation.betaContributors.0.contributors")
const getGroupedContributorsData = state => get(state, "global.data.compositeGroupedBetaContributors.data.model.simulation.groupedBetaContributors.0.groupedContributors")
const getSelectedGroupId = createQueryStringSelector("contributorGroup")
const getCurrentGrouping = (_, props) => get(props, "currentGrouping")
const getOrder = (_, props) => get(props, "sortOrder", EMPTY_OBJECT)
const getClassifications = (_, props) => props.classifications
const getSelectedDate = (_, props) => props.selectedDate
export const getSelectedContributorCategory = (_, props) => props.selectedContributorCategory
export const getGroupBy = (_, props) => get(props, "currentGrouping.id")

function computeNotHeldBucket(contributors) {
  return chain(contributors)
    .filter(item => !!item.notHeld)
    .reduce((acc, value) => {
      acc.size += value.percentEquity

      // Beta can be null for some securities
      const predicted = get(value, "predicted")
      if (predicted != null) {
        acc.factors.predicted = predicted + (acc.factors.predicted || 0)
      }
      const historical = get(value, "historical")
      if (historical != null) {
        acc.factors.historical = historical + (acc.factors.historical || 0)
      }

      return acc
    }, {
      id: "NOT HELD",
      notHeld: true,
      size: 0,
      factors: {},
    })
    .value()
}

function mapBetaContributors(data, showNotHeld) {
  const result = chain(data)
    .filter(item => showNotHeld || !item.notHeld)
    .map(security => {
      const { ticker, mic: exchange } = getTickerAndMicFromContributor(security)

      return {
        id: security.id,
        country: security.country,
        description: security.description,
        classification: get(security, "classification.name"),
        currency: security.currency,
        assetClass: security.assetClass,
        assetSubclass: security.assetSubclass,
        size: security.percentEquity,
        notHeld: security.notHeld,
        bloombergTicker: security.bloombergTicker,
        modelProviderId: security.modelProviderId,
        factors: {
          predicted: security.predicted,
          historical: security.historical,
        },
        ticker,
        exchange,
      }
    })
    .value()

  if (!showNotHeld) {
    result.push(computeNotHeldBucket(data))
  }
  return result
}

export const getSelectedGroup = createSelector(
  [getSelectedGroupId, getGroupedContributorsData],
  (selectedGroupId, contributors) => computeSelectedGroup({ id: selectedGroupId }, contributors),
)

export const getIncludeNotHeld = createSelector(
  [getViewType],
  (viewType) => viewType === VIEW_TYPES.ACTIVE,
)

const getSelectedField = createSelector(
  [getSelectedContributorCategory],
  (category) => get(category, "field", "total"),
)

const getContributors = createSelector(
  [getContributorsData, getShowNotHeld],
  mapBetaContributors,
)

const getFilteredContributors = createSelector(
  [getContributors, getSelectedField],
  (data, selectedField) =>
    filter(data, security => {
      return get(security, selectedField)
    }),
)

export const getSortedContributors = createSelector(
  [getFilteredContributors, getSelectedField, getOrder],
  (data, selectedField, order) => {
    if (data.length === 0) return EMPTY_ARRAY

    return orderBy(data, [
      (security) => {
        switch (order.by) {
          case "name":
            return security.id
          case "equity":
            return order.abs ? Math.abs(security.size) : security.size
          default: {
            const value = get(security, selectedField)
            return order.abs && isNumber(value) ? Math.abs(value) : value
          }
        }
      },
    ], [order.dir])
  },
)

const getGroupedContributors = createSelector(
  [getGroupedContributorsData, getShowNotHeld],
  (data, showNotHeld) => map(data, group => ({
    ...group,
    factors: {
      historical: group.historical,
      predicted: group.predicted,
    },
    contributors: mapBetaContributors(group.contributors, showNotHeld),
  })),
)

const getFilteredGroupedContributors = createSelector(
  [getGroupedContributors, getSelectedField, getSelectedGroup],
  (data, selectedField, selectedGroup) => {
    return chain(data)
      .filter(group => !selectedGroup || selectedGroup.id === group.id)
      .map(group => ({
        ...group,
        contributors: filter(group.contributors, security => get(security, selectedField)),
      }))
      .value()
  },
)

export const getSortedGroupContributors = createSelector(
  [getFilteredGroupedContributors, getSelectedField, getOrder],
  (data, selectedField, order) => {
    if (data.length === 0) return EMPTY_ARRAY

    return chain(data)
      .map(group => ({
        ...group,
        value: get(group, selectedField),
        contributors: orderBy(group.contributors, [
          (security) => {
            switch (order.by) {
              case "name":
                return security.id
              case "equity":
                return order.abs ? Math.abs(security.size) : security.size
              default: {
                const value = get(security, selectedField)
                return order.abs && isNumber(value) ? Math.abs(value) : value
              }
            }
          },
        ], [order.dir]),
      }))
      .orderBy(...makeGroupSort(order))
      .value()
  },
)

const getAccountRollups = createSelector(
  [getClassifications],
  (classifications) => ({
    classifications,
  }),
)

export const getAvailableRollups = createSelector(
  [getAccountRollups, getSelectedDate, getViewType],
  getAllAvailableRollups,
)

export const getContributorsSummaryText = createSelector(
  [getCurrentGrouping, getSortedContributors, getSortedGroupContributors, getSelectedGroup, getShowNotHeld, getViewType],
  positionSetContributorsSummaryText,
)

export const getExcelData = createSelector(
  [getSortedContributors, getSortedGroupContributors, getSelectedContributorCategory, getCurrentGrouping, getPositionSetName, getSelectedDate, getViewType, getSelectedGroup, getFactorCategoryName],
  (data, groupedData, contributorCategory, contributorsGrouping, positionSetName, selectedDate, viewType, selectedGroup, factorCategory) => {
    if (selectedGroup) {
      const group = first(groupedData)
      return computeGroupedContributorsCSV({
        type: factorCategory,
        portfolioName: positionSetName,
        selected: contributorCategory,
        selectedDate,
        viewType,
        group,
        net: true,
      })
    }

    if (contributorsGrouping) {
      return computeContributorsGroupCSV({
        type: factorCategory,
        portfolioName: positionSetName,
        selected: contributorCategory,
        selectedDate,
        viewType,
        contributorsGrouping,
        data: groupedData,
        net: true,
      })
    }

    return computeContributorsCSV({
      type: factorCategory,
      portfolioName: positionSetName,
      selected: contributorCategory,
      selectedDate,
      viewType,
      data,
      net: true,
    })
  },
)

export const getSelectedGroupValue = createSelector(
  [getSelectedGroup, getGroupedContributors, getSelectedField],
  (selectedGroup, groupedContributors, selectedField) => {
    if (selectedGroup) {
      const group = find(groupedContributors, { id: selectedGroup.id })
      return get(group, selectedField)
    }

    return null
  },
)

export const getSelectedGroupSiblings = createSelector(
  [getSelectedGroup, getGroupedContributors, getSelectedField],
  (selectedGroup, groupedContributors, selectedField) => {
    if (selectedGroup) {
      return chain(groupedContributors)
        .map(group => ({
          ...group,
          value: get(group, selectedField),
        }))
        .orderBy(["name"], ["asc"])
        .value()
    }

    return null
  },
)
